import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Ambulance, Phone, X, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const isMobileDevice = () => {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  const coarse =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches;
  return (
    coarse ||
    /android|iphone|ipod|ipad|windows phone|mobile/i.test(ua.toLowerCase())
  );
};

export default function EmergencyButton() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();

  const handleCall = () => {
    if (isMobileDevice()) {
      window.location.href = "tel:108";
      setOpen(false);
    } else {
      toast.error(
        "Direct calling is available only on mobile devices. Please dial 108 from your nearest phone.",
        { duration: 6000 }
      );
    }
  };

  return (
    <>
      <button
        data-testid="emergency-sos-btn"
        onClick={() => setOpen(true)}
        aria-label="Emergency SOS — Call 108"
        className={`emergency-sos-btn fixed z-[60] right-4 md:right-8 md:bottom-32 group ${
          user
            ? "bottom-[calc(5.5rem+env(safe-area-inset-bottom))]"
            : "bottom-6"
        }`}
      >
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full bg-[#E53935] emergency-sos-pulse"
        />
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full bg-[#E53935] emergency-sos-pulse delay-200"
        />
        <span className="relative inline-flex items-center justify-center gap-2 w-12 h-12 md:w-auto md:h-auto md:pl-4 md:pr-5 md:py-3 rounded-full bg-[#E53935] hover:bg-[#c62828] active:bg-[#b71c1c] shadow-[0_10px_28px_rgba(229,57,53,0.45)] border border-white/10 text-white transition-transform group-hover:scale-[1.03]">
          <Ambulance className="w-5 h-5" />
          <span className="hidden md:inline text-sm font-semibold tracking-tight">
            Emergency
          </span>
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogPortal>
          <DialogOverlay className="bg-black/80 backdrop-blur-md z-[70]" />
          <DialogContent
            data-testid="emergency-dialog"
            className="fixed left-[50%] top-[50%] z-[70] w-[92vw] max-w-md -translate-x-1/2 -translate-y-1/2 p-0 border-0 bg-transparent shadow-none [&>button.absolute]:hidden"
          >
            <div className="relative overflow-hidden rounded-3xl glass-card border border-red-400/25 bg-[#0a0708]/95 shadow-[0_40px_120px_rgba(229,57,53,0.35)]">
              <DialogTitle className="sr-only">Medical Emergency</DialogTitle>
              <DialogDescription className="sr-only">
                Confirm before contacting the Indian Medical Emergency Ambulance
                Service on 108.
              </DialogDescription>

              <button
                data-testid="emergency-dialog-close"
                onClick={() => setOpen(false)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center bg-white/[0.04] border border-white/10 hover:border-red-400/40 transition-colors z-10"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="p-7 sm:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-red-500/15 border border-red-400/40 flex items-center justify-center shrink-0">
                    <ShieldAlert className="w-7 h-7 text-red-400" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-red-300 font-semibold">
                      Medical Emergency
                    </div>
                    <h3 className="mt-1 font-display text-2xl font-semibold tracking-tight text-white">
                      Are you sure you want to call 108?
                    </h3>
                  </div>
                </div>

                <p className="mt-5 text-sm text-zinc-300 leading-relaxed">
                  You are about to contact the{" "}
                  <span className="text-white font-medium">
                    Indian Medical Emergency Ambulance Service (108)
                  </span>
                  . If this is a genuine medical emergency, press{" "}
                  <span className="text-red-300 font-medium">Call 108</span>{" "}
                  immediately.
                </p>

                <div className="mt-8 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
                  <button
                    data-testid="emergency-cancel-btn"
                    onClick={() => setOpen(false)}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-medium btn-outline-glass text-zinc-200"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                  <a
                    data-testid="emergency-call-btn"
                    href="tel:108"
                    onClick={(e) => {
                      if (!isMobileDevice()) {
                        e.preventDefault();
                        handleCall();
                      } else {
                        setOpen(false);
                      }
                    }}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white bg-[#E53935] hover:bg-[#c62828] active:bg-[#b71c1c] shadow-[0_10px_28px_rgba(229,57,53,0.45)] transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    Call 108
                  </a>
                </div>

                <p className="mt-6 text-[11px] text-zinc-500 leading-relaxed border-t border-white/5 pt-4">
                  On desktop, direct dialling isn't available — please dial 108
                  from your nearest phone.
                </p>
              </div>
            </div>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </>
  );
}
